import {
  locationIdFormat,
  sortDestinationIdFormat,
  sortingToteIdFormat,
  stationProcessingSessionIdFormat,
  stationProcessingSessionToteIdFormat,
} from "@redotech/redo-ids-cockroach";
import { SortDispositionSchema } from "@redotech/redo-model/wms/schema/sort-disposition";
import { StationProcessingSessionCloseReasonSchema } from "@redotech/redo-model/wms/schema/station-processing-session";
import * as z from "zod";

/**
 * Display-ready projection of an active tote↔destination binding. The session
 * response carries these so the operator UI can restore the cash register —
 * which totes are bound to which destinations — without resolving names itself.
 */
export const StationProcessingSessionToteBinding = z.object({
  id: stationProcessingSessionToteIdFormat.schema,
  sortingToteId: sortingToteIdFormat.schema,
  sortingToteName: z.string(),
  sortDestinationId: sortDestinationIdFormat.schema,
  sortDestinationName: z.string(),
  attachedAt: z.date(),
});
export type StationProcessingSessionToteBinding = z.infer<
  typeof StationProcessingSessionToteBinding
>;

/**
 * Display-ready active tote binding for a station session: the binding joined to
 * its tote name and destination (name + disposition), plus the tote's current
 * item count. Shared by the bind and sort RPC responses (BFF).
 */
export const StationProcessingSessionToteBindingViewSchema = z.object({
  id: stationProcessingSessionToteIdFormat.schema,
  sortingToteId: sortingToteIdFormat.schema,
  toteName: z.string(),
  sortDestinationId: sortDestinationIdFormat.schema,
  destinationName: z.string(),
  disposition: SortDispositionSchema,
  quantity: z.number(),
});
export type StationProcessingSessionToteBindingView = z.infer<
  typeof StationProcessingSessionToteBindingViewSchema
>;

export const StationProcessingSession = z.object({
  id: stationProcessingSessionIdFormat.schema,
  workstationId: z.string(),
  locationId: locationIdFormat.schema,
  openedAt: z.date(),
  closedAt: z.date().nullable(),
  closeReason: StationProcessingSessionCloseReasonSchema.nullable(),
  bindings: z.array(StationProcessingSessionToteBinding),
});
export type StationProcessingSession = z.infer<typeof StationProcessingSession>;
